/**
 * # Commit referee — the symmetric semantic comparator of two commit bundles
 *
 * The default-column live commit computes TWO structure programs every full
 * rebuild: the positional `compileCommitBundle` (Levenshtein edit script) and
 * the scene-driven `compileSceneCommit` (LCS-anchored, survivor-preserving). One
 * feeds the DOM; the other rides dark. This referee is the oracle that decides
 * whether the dark bundle is a SAFE alternative to the live one.
 *
 * It compares OUTCOMES, not op streams: each bundle's structure program is
 * replayed against a pure mirror of the pre-apply cell-pool `rid` snapshot, and
 * the two resulting outcomes are compared on four axes:
 *
 *   (a) final key arrangement,
 *   (b) mount key multiset,
 *   (c) removed-occupant multiset (occupant = key-or-"" resolved at drain),
 *   (d) recycled (occupant → newKey) set.
 *
 * Verdicts:
 *   - `exact`            — all four axes identical (byte-outcome-equal).
 *   - `slide-classified` — final arrangements EQUAL and both programs are lawful
 *     re-expressions of that arrangement which differ only in WHICH SURVIVORS
 *     they keep: whose cell is reused for which row. The benign divergence
 *     (the scene's value) — the DOM is the same either way, only bound content moves
 *     between cells. Decided by decomposing the symmetric difference of the two
 *     edge structures; see {@link isSlide}.
 *   - `loud`             — anything else: a different final arrangement (a real
 *     correctness bug), or a same-final divergence that does NOT decompose —
 *     a cell reused that no pool slot held, a row produced that the target
 *     never asked for, or one cell given two fates by one program.
 *
 * Symmetric: swapping the two bundles yields the same verdict. Pure + DOM-free
 * (no imports beyond the `CommitBundle` type), so it loads under Deno alongside
 * `compileCommitBundle` / `compileSceneCommit`.
 */

import { nonnull } from "@tento-core/assert";
import type { Option } from "@tento-lona/sheets";
import type { CellDirective, CommitBundle, CommitStructureOp } from "./commit-bundle.ts";
import { multisetEqual } from "./commit-bundle.ts";

/**
 * Edge delimiter for the `occupant→newKey` recycle strings — an ASCII unit
 * separator (`U+001F`), which never appears in a row wire-string, so the split
 * is unambiguous even when a key contains `:` / `~` / `|`.
 */
const EDGE = "\u001f";

/** How a commit referee judged two outcomes: `exact`, `slide-classified`, or `loud` (they diverged). */
export type RefereeVerdict = "exact" | "slide-classified" | "loud";

/** A bounded description of the first axis on which two outcomes diverged. */
export interface RefereeDivergence {
  readonly axis: "final-arrangement" | "mounts" | "removes" | "recycles";
  /** ≤120-char human-readable summary of the mismatch (safe to log). */
  readonly detail: string;
}

/**
 * The commit referee's verdict on one bind: whether the dark structure program matches the live
 * one, and the first divergence if not.
 */
export interface RefereeResult {
  readonly verdict: RefereeVerdict;
  readonly firstDivergence: Option<RefereeDivergence>;
}

/**
 * Referee bundle `a` (canonically the positional) against bundle `b` (the scene)
 * over the pre-apply pool `rid` snapshot. Symmetric in `a`/`b` for the verdict;
 * `firstDivergence` describes the mismatch from `a`'s vantage.
 */
export function refereeCommit(
  poolRids: readonly Option<string>[],
  a: CommitBundle,
  b: CommitBundle,
): RefereeResult {
  const oa = replayStructure(poolRids, a.structure);
  const ob = replayStructure(poolRids, b.structure);

  if (!finalEqual(oa.final, ob.final)) {
    return {
      verdict: "loud",
      firstDivergence: {
        axis: "final-arrangement",
        detail: bounded(`a=[${keysOf(oa.final)}] b=[${keysOf(ob.final)}]`),
      },
    };
  }

  const mountsEqual = multisetEqual(oa.mounts, ob.mounts);
  const removesEqual = multisetEqual(oa.removes, ob.removes);
  const recyclesEqual = setEqual(oa.recycles, ob.recycles);
  if (mountsEqual && removesEqual && recyclesEqual) {
    return { verdict: "exact", firstDivergence: null };
  }

  if (isSlide(poolRids, oa, ob)) {
    return { verdict: "slide-classified", firstDivergence: null };
  }

  // Final arrangements agree but the divergence is not a benign slide.
  const divergence = firstStructuralDivergence(oa, ob, mountsEqual, removesEqual);
  return { verdict: "loud", firstDivergence: divergence };
}

/** Outcome of replaying one structure program against a pool mirror. */
interface Outcome {
  readonly final: readonly Option<string>[];
  readonly mounts: readonly string[];
  /** Removed occupants at drain (`""` for a null-rid slot). */
  readonly removes: readonly string[];
  /** `occupant→newKey` recycle edges (occupant `""` for a null-rid slot). */
  readonly recycles: readonly string[];
}

function replayStructure(
  poolRids: readonly Option<string>[],
  structure: readonly CommitStructureOp[],
): Outcome {
  const mirror: Option<string>[] = [...poolRids];
  const mounts: string[] = [];
  const removes: string[] = [];
  const recycles: string[] = [];
  for (const op of structure) {
    switch (op.tag) {
      case "mount":
        mounts.push(op.key);
        mirror.splice(op.slot, 0, op.key);
        break;
      case "recycle": {
        const occ = mirror[op.slot] ?? "";
        recycles.push(`${occ}${EDGE}${op.key}`);
        mirror[op.slot] = op.key;
        break;
      }
      case "remove": {
        const occ = mirror[op.slot] ?? "";
        removes.push(occ);
        mirror.splice(op.slot, 1);
        break;
      }
      case "keep":
        break;
    }
  }
  return { final: mirror, mounts, removes, recycles };
}

/**
 * The slide test — the benign same-final divergence, as a decomposition.
 *
 * By the time this runs the two programs already agree on the final
 * arrangement. What is left is a disagreement about CELL IDENTITY: which pool
 * cell is reused for which target row — whose bound content survives. That is
 * benign (the DOM is identical either way), but only when both programs are
 * lawful re-expressions of that one arrangement. This decides exactly that.
 *
 * ## The graph
 * Read each program as edges over the journeys of individual cells:
 *
 * ```text
 *   recycle  src→dst    the cell that entered holding `src` leaves holding `dst`
 *   mount      ∅→key    a fresh cell arrives holding `key`
 *   remove     key→∅    the cell holding `key` leaves
 *   keep      (no edge) the cell stays where it is, still holding its own row
 * ```
 *
 * Split every row into its two ENDS — `s:key` (the cell that ENTERED holding it)
 * and `d:key` (the cell that LEAVES holding it) — so each end carries at most one
 * edge per program. Take the SYMMETRIC DIFFERENCE (the edges one program has and
 * the other does not). Because of that degree bound its components are chains and
 * cycles by construction, alternating between the two programs — so validating
 * the decomposition reduces to validating every END it touches, which is what the
 * loop below does. No orientation is chosen: the difference is symmetric, so the
 * verdict is symmetric without trying the pair both ways round.
 *
 * ## What every end must prove — "accounted for on both sides"
 *   - `s:k` requires `k` to have been a POOL occupant: a cell can only be reused
 *     or removed if it existed. A side with NO edge at `s:k` kept that cell in
 *     place, which additionally requires `k` to be a target row.
 *   - `d:k` requires `k` to be a TARGET row: no program may produce a row nobody
 *     asked for. A side with no edge at `d:k` kept it, so `k` must have been in
 *     the pool already.
 *   - the `∅` end of a mount / remove is FREE — it terminates a chain. So is a
 *     never-bound (null-rid) cell: it holds no content, so a disagreement about
 *     which empty cell to reuse can lose nothing.
 *
 * ## The two minimized pairs (the whole `loud` class, worked)
 * `[A,B] → [B,A]`. Positional keeps nothing — `A→B`, `B→A`, a 2-cycle of
 * recycles; the scene keeps A and expresses the same arrangement as mount B +
 * remove B. Two chains, each ending on a keep:
 *
 * ```text
 *   ∅mount —scene— d:B —positional— s:A      s:A silent on the scene side ⇒ A kept
 *   ∅remove —scene— s:B —positional— d:A     d:A silent on the scene side ⇒ A kept
 * ```
 *
 * `[A,B,C,D] → [C,B]`. Positional keeps C (recycles D's cell → B, removes A's and
 * B's); the scene keeps B (recycles A's cell → C, removes C's and D's). Disjoint
 * remove sets, four chains, every end accounted:
 *
 * ```text
 *   ∅remove —pos— s:A —scene— d:C            d:C silent on the positional side ⇒ C kept
 *   d:B —pos— s:D —scene— ∅remove            d:B silent on the scene side      ⇒ B kept
 *   s:B —pos— ∅remove                        s:B silent on the scene side      ⇒ B kept
 *   s:C —scene— ∅remove                      s:C silent on the positional side ⇒ C kept
 * ```
 *
 * ## What stays loud
 * A DIFFERENT FINAL ARRANGEMENT never reaches here — it is judged before the
 * decomposition and is always loud. Beyond that, a same-final divergence is loud
 * when a component holds an UNACCOUNTED end: a recycle whose source no pool slot
 * ever held (the phantom hop `A→Z`, `Z→B` through a key nobody asked for, which
 * re-expresses `[A,B]→[B,A]` while routing a cell through a row that does not
 * exist), a mount or recycle producing a row absent from the target, or one
 * program giving a single cell two fates — not a matching, so not a slide.
 */
function isSlide(
  poolRids: readonly Option<string>[],
  a: Outcome,
  b: Outcome,
): boolean {
  const edges = [...divergentEdges(a, b, "a"), ...divergentEdges(b, a, "b")];
  if (edges.length === 0) return false; // no divergence to explain

  const ends = new Map<string, Incidence>();
  for (const edge of edges) {
    if (!attach(ends, edge.src, edge)) return false;
    if (!attach(ends, edge.dst, edge)) return false;
  }

  const frame: SurvivorFrame = {
    pool: new Set(poolRids.filter((k): k is string => k != null)),
    target: new Set(a.final.filter((k): k is string => k != null)),
  };
  for (const [end, incidence] of ends) {
    if (!endAccounted(frame, end, incidence)) return false;
  }
  return true;
}

/** The rows the pool held and the rows the target wants — the accounting frame. */
interface SurvivorFrame {
  readonly pool: ReadonlySet<string>;
  readonly target: ReadonlySet<string>;
}

/**
 * One end of one cell's journey: `s:<key>` (entered holding it) or `d:<key>`
 * (leaves holding it). `null` is a FREE end — the `∅` of a mount / remove, or a
 * never-bound cell. Both prefixes are two characters wide, so the row key is
 * always `slice(2)`.
 */
const SOURCE_END = "s:";
const DEST_END = "d:";

/** An edge of the symmetric difference, owned by exactly one of the programs. */
interface DivergenceEdge {
  readonly side: "a" | "b";
  readonly src: Option<string>;
  readonly dst: Option<string>;
}

/** The edges incident to one end — at most one per program, or it is no matching. */
interface Incidence {
  a: Option<DivergenceEdge>;
  b: Option<DivergenceEdge>;
}

/** `side`'s edges that `other` does not have, as journey ends. */
function divergentEdges(
  side: Outcome,
  other: Outcome,
  owner: "a" | "b",
): DivergenceEdge[] {
  const edges: DivergenceEdge[] = [];
  for (const recycle of setDiff(side.recycles, other.recycles)) {
    const [src, dst] = splitEdge(recycle);
    edges.push({ side: owner, src: sourceEnd(src), dst: DEST_END + dst });
  }
  for (const key of multisetDiff(side.mounts, other.mounts)) {
    edges.push({ side: owner, src: null, dst: DEST_END + key });
  }
  for (const occupant of multisetDiff(side.removes, other.removes)) {
    edges.push({ side: owner, src: sourceEnd(occupant), dst: null });
  }
  return edges;
}

/** A never-bound (null-rid) occupant has no identity to preserve: a free end. */
function sourceEnd(occupant: string): Option<string> {
  return occupant === "" ? null : SOURCE_END + occupant;
}

/** Hang `edge` on `end`; false when that end already has a fate on this side. */
function attach(
  ends: Map<string, Incidence>,
  end: Option<string>,
  edge: DivergenceEdge,
): boolean {
  if (end == null) return true; // free ends terminate a chain and hold nothing
  const incidence = ends.get(end) ?? { a: null, b: null };
  if (edge.side === "a") {
    if (incidence.a != null) return false;
    incidence.a = edge;
  } else {
    if (incidence.b != null) return false;
    incidence.b = edge;
  }
  ends.set(end, incidence);
  return true;
}

/** Is this end explained by BOTH programs? (The chain/cycle endpoint rule.) */
function endAccounted(frame: SurvivorFrame, end: string, incidence: Incidence): boolean {
  const key = end.slice(SOURCE_END.length);
  // A silent side has no edge here, which can only mean it KEPT the cell — and a
  // kept cell is one the pool held and the target still wants.
  const silent = incidence.a == null || incidence.b == null;
  if (end.startsWith(SOURCE_END)) {
    return frame.pool.has(key) && (!silent || frame.target.has(key));
  }
  return frame.target.has(key) && (!silent || frame.pool.has(key));
}

function firstStructuralDivergence(
  a: Outcome,
  b: Outcome,
  mountsEqual: boolean,
  removesEqual: boolean,
): RefereeDivergence {
  // Report the symmetric difference, not the two whole multisets: with a dozen
  // wire-string keys per side the full sets blow the bound and the `b` half was
  // always the part that got truncated — the reader was left knowing only that
  // the sides differed. `a-only` / `b-only` is what a diagnosis actually needs
  // and stays well inside the bound.
  if (!mountsEqual) {
    return {
      axis: "mounts",
      detail: bounded(
        `a-only={${sorted(multisetDiff(a.mounts, b.mounts))}} ` +
          `b-only={${sorted(multisetDiff(b.mounts, a.mounts))}} ` +
          `(a=${a.mounts.length} b=${b.mounts.length})`,
      ),
    };
  }
  if (!removesEqual) {
    return {
      axis: "removes",
      detail: bounded(
        `a-only={${sorted(multisetDiff(a.removes, b.removes))}} ` +
          `b-only={${sorted(multisetDiff(b.removes, a.removes))}} ` +
          `(a=${a.removes.length} b=${b.removes.length})`,
      ),
    };
  }
  return {
    axis: "recycles",
    detail: bounded(`a={${readableEdges(a.recycles)}} b={${readableEdges(b.recycles)}}`),
  };
}

function finalEqual(a: readonly Option<string>[], b: readonly Option<string>[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if ((a[i] ?? null) !== (b[i] ?? null)) return false;
  }
  return true;
}

function setEqual(a: readonly string[], b: readonly string[]): boolean {
  const sa = new Set(a);
  const sb = new Set(b);
  if (sa.size !== sb.size) return false;
  for (const k of sa) if (!sb.has(k)) return false;
  return true;
}

/** Multiset `a` minus multiset `b` (elements of `a` not covered by `b`). */
function multisetDiff(a: readonly string[], b: readonly string[]): string[] {
  const counts = new Map<string, number>();
  for (const k of b) counts.set(k, (counts.get(k) ?? 0) + 1);
  const out: string[] = [];
  for (const k of a) {
    const c = counts.get(k) ?? 0;
    if (c > 0) counts.set(k, c - 1);
    else out.push(k);
  }
  return out;
}

/** Set `a` minus set `b`. */
function setDiff(a: readonly string[], b: readonly string[]): string[] {
  const sb = new Set(b);
  return [...new Set(a)].filter((k) => !sb.has(k));
}

function splitEdge(edge: string): [string, string] {
  const idx = edge.indexOf(EDGE);
  return [edge.slice(0, idx), edge.slice(idx + 1)];
}

function keysOf(arr: readonly Option<string>[]): string {
  return arr.map((k) => k ?? "∅").join(",");
}

function sorted(arr: readonly string[]): string {
  return [...arr].sort().map((k) => (k === "" ? "∅" : k)).join(",");
}

function readableEdges(edges: readonly string[]): string {
  return [...edges]
    .sort()
    .map((e) => {
      const [src, dst] = splitEdge(e);
      return `${src === "" ? "∅" : src}→${dst}`;
    })
    .join(",");
}

function bounded(s: string): string {
  return s.length <= 120 ? s : `${s.slice(0, 117)}...`;
}

// ── the law both structural compilers obey ─────────────────────────

/**
 * The name of one law a structure program can break. Each is a property the
 * REST of the system already assumes — `refereeCommit` compares two programs to
 * each other, but nothing checked either against the pool and targets it was
 * compiled from.
 *
 * - `final` — replaying the ops over the pool must yield exactly `targetKeys`.
 *   The applier's whole contract: the DOM after the commit IS the target.
 * - `suffix-minimal-mounts` — nothing is mounted that a TAIL-ALIGNED pairing of
 *   dead pool slots to needed target positions could have recycled. See
 *   {@link checkStructureLaws} for why the pairing is derived from the program's
 *   own survivors.
 * - `duplicate-pool-rids` / `duplicate-target-keys` — the preconditions
 *   `compileSceneCommit` fails loudly on (`SceneCommitFailure`) and the ones
 *   `replayStructure` + the slide test silently assume: the decomposition in
 *   {@link isSlide} is only valid because row uniqueness bounds each journey end
 *   to one edge per program, which is what makes its components chains and
 *   cycles. A duplicate breaks that and the divergence is refused, not
 *   classified.
 */
export type StructureLawName =
  | "final"
  | "suffix-minimal-mounts"
  | "duplicate-pool-rids"
  | "duplicate-target-keys";

/** One broken law, as data a fuzzer can print, minimize against, and file. */
export interface StructureLaw {
  readonly law: StructureLawName;
  /** ≤120-char human-readable summary of the breach (safe to log). */
  readonly detail: string;
}

/**
 * Check one structure program against the pool and targets it was compiled
 * from. Returns the laws it BREAKS; an empty array is the lawful case.
 *
 * Pure and compiler-agnostic: `compileCommitBundle` (positional Levenshtein
 * replay) and `compileSceneCommit` (LCS anchors + gap pairing) must both satisfy
 * it, which is what makes it a law rather than a second opinion. It is the
 * missing half of `refereeCommit`: the referee says "these two programs agree",
 * this says "this program is correct on its own terms". A fuzzer needs both —
 * two compilers can agree on a wrong answer.
 *
 * ## Why the mount law is conditional on the program's own survivors
 * "Tail-aligned" (`commit-bundle.ts:296-311`, the 2026-08-31 fix) is a statement
 * about the PAIRING inside a gap, not about which cells survive. The two
 * compilers legitimately choose different survivor sets — Levenshtein prefers
 * `replace` at every step, so a reversal keeps nothing, while the LCS backtrack
 * keeps one end — and that choice is the referee's business (it is what a
 * `slide-classified` verdict IS), not the law's. So the expected mount multiset
 * is derived from the program's own `keep` ops: given THOSE survivors as
 * anchors, the dead pool slots and the needed target positions between two
 * consecutive anchors are paired from the END of the run, and only the leftover
 * needed positions at the FRONT may be mounted. A front-aligned pairing — the
 * exact 08-31 defect — mounts the tail key where the law expects the head key,
 * so it breaks this law over any gap whose two sides are unequal, which is
 * precisely the input the exact battery never generated.
 */
export function checkStructureLaws(
  pool: readonly Option<string>[],
  targetKeys: readonly string[],
  ops: readonly CommitStructureOp[],
): readonly StructureLaw[] {
  const laws: StructureLaw[] = [];

  const poolKeys = pool.filter((k): k is string => k != null);
  const dupPool = firstDuplicate(poolKeys);
  if (dupPool != null) {
    laws.push({
      law: "duplicate-pool-rids",
      detail: bounded(`rid ${dupPool} occupies two pool slots: [${keysOf(pool)}]`),
    });
  }
  const dupTarget = firstDuplicate(targetKeys);
  if (dupTarget != null) {
    laws.push({
      law: "duplicate-target-keys",
      detail: bounded(`key ${dupTarget} listed twice in targets: [${targetKeys.join(",")}]`),
    });
  }

  const outcome = replayStructure(pool, ops);
  if (!finalEqual(outcome.final, targetKeys)) {
    laws.push({
      law: "final",
      detail: bounded(`replay=[${keysOf(outcome.final)}] target=[${keysOf(targetKeys)}]`),
    });
  }

  // A duplicate key makes the anchor/membership math undercount — the same
  // reason `compileSceneCommit` refuses to compile at all — so the mount
  // expectation below would be arithmetic on a lie. The duplicate law is the
  // finding; do not stack a derived one on top of it.
  if (dupPool != null || dupTarget != null) return laws;

  const expected = expectedMounts(pool, targetKeys, keptKeys(ops));
  if (!multisetEqual(expected, outcome.mounts)) {
    laws.push({
      law: "suffix-minimal-mounts",
      detail: bounded(
        `mounted={${sorted(outcome.mounts)}} tail-aligned pairing allows {${sorted(expected)}}`,
      ),
    });
  }
  return laws;
}

/** The rows a program declares SURVIVORS — the cells it leaves untouched. */
function keptKeys(ops: readonly CommitStructureOp[]): ReadonlySet<string> {
  const kept = new Set<string>();
  for (const op of ops) if (op.tag === "keep") kept.add(op.key);
  return kept;
}

/**
 * The mount multiset a tail-aligned pairing permits, given `kept` as the
 * surviving anchors. Independently derived from pool + targets — deliberately
 * NOT a call into either compiler, so a compiler that changes its pairing
 * disagrees with the law instead of redefining it.
 */
function expectedMounts(
  pool: readonly Option<string>[],
  targetKeys: readonly string[],
  kept: ReadonlySet<string>,
): string[] {
  const n = pool.length;
  const m = targetKeys.length;
  const anchorPool: boolean[] = [];
  for (let i = 0; i < n; i++) {
    const rid = pool[i] ?? null;
    anchorPool.push(rid != null && kept.has(rid));
  }
  const anchorTarget = targetKeys.map((k) => kept.has(k));

  const mounts: string[] = [];
  let pi = 0;
  let tj = 0;
  while (pi < n || tj < m) {
    let dead = 0;
    const need: number[] = [];
    while (pi < n && !anchorPool[pi]) {
      dead += 1;
      pi += 1;
    }
    while (tj < m && !anchorTarget[tj]) {
      need.push(tj);
      tj += 1;
    }
    // Paired from the END of the gap; the leftover FRONT positions are the only
    // ones a lawful program may mount.
    const paired = Math.min(dead, need.length);
    for (const slot of need.slice(0, need.length - paired)) {
      mounts.push(nonnull(targetKeys[slot], "commit referee: a needed target slot"));
    }
    if (pi < n && anchorPool[pi]) pi += 1;
    if (tj < m && anchorTarget[tj]) tj += 1;
  }
  return mounts;
}

/** The first key that appears twice, or null when every key is distinct. */
function firstDuplicate(keys: readonly string[]): Option<string> {
  const seen = new Set<string>();
  for (const k of keys) {
    if (seen.has(k)) return k;
    seen.add(k);
  }
  return null;
}

// ── per-cell directive referee ─────────────────────────────────────

/**
 * A cheap output-identity fingerprint of a cell — structurally identical to the
 * binder's `DefaultBinder.CellFingerprint` (kept local so this module stays
 * DOM-free and Deno-loadable). Captures the structural facts a `skip` / `resize`
 * / `span` claim asserts unchanged: mounted content type, hidden state, bound
 * height, and whether a content element is present.
 */
export interface CellFingerprint {
  readonly contentType: Option<string>;
  readonly hidden: boolean;
  readonly height: Option<number>;
  readonly hasContent: boolean;
}

/**
 * What the LEDGER can testify about a row the classifier claims is unchanged —
 * the always-on layer-1 signal, meaningful even while the render cursor is dark.
 *
 * This was a bare `cursorWouldRepaint: boolean` read off
 * `!RenderSkip.decide().wouldSkip`, and that conflated a verdict with a
 * conservative default. `wouldSkip` is false for three different reasons and
 * only ONE of them says the classifier got it wrong:
 *
 *   - the row has a record and its versions MOVED  → `would-repaint`, a real
 *     divergence: the scene diff called it unchanged and the ledger disagrees;
 *   - the row was never bound before               → `no-opinion / never-bound`;
 *   - the row's output node is unresolvable        → `no-opinion / unresolvable`:
 *     pending, template, system and CONTAINER rows have no data address at all,
 *     so `currentVersions` is null on every bind, forever.
 *
 * The last two are the ledger declining to answer, not a judgment, and the
 * pipeline already rebinds those rows anyway (`canSkip` requires
 * `shouldRebind === false`, which a null version compare never satisfies).
 * Reporting them as divergences made the referee cry wolf on every bind —
 * measured 2026-08-25, 106 loud verdicts whose divergence was `skip@0` every
 * time, index 0 being the sheet's `alias.calendar` container row — and because
 * the SDK 2.0 re-promotion gate is "referee at zero", it made that gate
 * unreachable by construction.
 */
export type CursorEvidence =
  /** Record present, versions moved: the classifier's `skip` is wrong. */
  | { readonly kind: "would-repaint" }
  /** Record present, versions match: the classifier's `skip` is right. */
  | { readonly kind: "agrees" }
  /** The ledger cannot speak for this row. Not evidence either way. */
  | { readonly kind: "no-opinion"; readonly why: CursorEvidence.NoOpinion };

export namespace CursorEvidence {
  export type NoOpinion =
    /** No cursor on this binder at all. */
    | "no-cursor"
    /** The row has never completed a bind, so there is no record to compare. */
    | "never-bound"
    /** Placement-only (pending / template / system / container): no data address. */
    | "unresolvable";
}

/**
 * One classified cell's evidence for the cell referee, aligned to `directives`
 * by `index`. `cursor` is the ALWAYS-ON layer-1 signal; `pre` / `post`
 * fingerprints straddle the STILL-EXECUTED full rebind and are present only for
 * LAYER-2-sampled cells (both `null` ⇒ no layer-2 verdict).
 */
export interface CellRefereeSample {
  /** The pre-apply DOM identity allowed resize/span to preserve its content. */
  readonly reflowHonorable: boolean;
  readonly index: number;
  readonly pre: Option<CellFingerprint>;
  readonly post: Option<CellFingerprint>;
  readonly cursor: CursorEvidence;
}

/** How a cell referee judged one cell commit: `agree`, or `loud` when the claim and the outcome differ. */
export type CellRefereeVerdict = "agree" | "loud";

/** A bounded description of the first cell whose claim the referee rejected. */
export interface CellRefereeDivergence {
  readonly index: number;
  readonly claim: CellDirective["tag"];
  readonly reason: "cursor-would-repaint" | "fingerprint-moved" | "content-moved";
  /** ≤120-char human-readable summary (safe to log). */
  readonly detail: string;
}

/** A cell referee's judgement of one cell commit: its verdict and the first divergence it found. */
export interface CellRefereeResult {
  readonly verdict: CellRefereeVerdict;
  readonly firstDivergence: Option<CellRefereeDivergence>;
}

/**
 * Referee the classified `directives` against the per-cell `samples` gathered
 * around the still-executed full rebind. Two layers (mirroring the structure
 * referee's verdict shape, `agree` | `loud` + a bounded `firstDivergence`):
 *
 *   - LAYER 1 (always, O(1), no fingerprints): a `skip` the ledger positively
 *     contradicts (`cursor.kind === "would-repaint"`) is loud. A `no-opinion`
 *     row — never bound, or placement-only, so it has no versions to compare —
 *     is NOT a divergence; see {@link CursorEvidence}.
 *   - LAYER 2 (sampled fingerprints): a `skip` whose fingerprint moved is loud; a
 *     eligible `resize` / `span` whose contentType or hasContent moved is loud
 *     (height may legitimately move under a reflow, so it is not compared).
 *     A pre-apply DOM identity refusal forces a full rebind; its restored
 *     content is not evidence that an honored reflow contradicted itself.
 *
 * `rebind` claims are never checked (a full rebind is always safe). Unsampled
 * cells (`pre`/`post` null) contribute only their layer-1 verdict.
 */
export function refereeCells(
  directives: readonly CellDirective[],
  samples: readonly CellRefereeSample[],
): CellRefereeResult {
  for (const sample of samples) {
    const directive = directives[sample.index] ?? null;
    if (directive == null) continue;
    const claim = directive.tag;

    // Layer 1 — always-on ledger cross-check for skips. Only a positive
    // `would-repaint` is a divergence; `no-opinion` is the ledger declining to
    // testify and must not be reported as the classifier being wrong.
    if (claim === "skip" && sample.cursor.kind === "would-repaint") {
      return loudCell(sample.index, claim, "cursor-would-repaint", bounded(
        `skip@${sample.index} but the render cursor would repaint this row`,
      ));
    }

    // Layer 2 — sampled fingerprints straddling the full rebind.
    if (sample.pre == null || sample.post == null) continue;
    if (claim === "skip") {
      if (fingerprintMoved(sample.pre, sample.post)) {
        return loudCell(sample.index, claim, "fingerprint-moved", bounded(
          `skip@${sample.index} fingerprint moved ${fpDetail(sample.pre, sample.post)}`,
        ));
      }
    } else if ((claim === "resize" || claim === "span") && sample.reflowHonorable) {
      if (
        sample.pre.contentType !== sample.post.contentType ||
        sample.pre.hasContent !== sample.post.hasContent
      ) {
        return loudCell(sample.index, claim, "content-moved", bounded(
          `${claim}@${sample.index} content moved ${fpDetail(sample.pre, sample.post)}`,
        ));
      }
    }
  }
  return { verdict: "agree", firstDivergence: null };
}

function loudCell(
  index: number,
  claim: CellDirective["tag"],
  reason: CellRefereeDivergence["reason"],
  detail: string,
): CellRefereeResult {
  return { verdict: "loud", firstDivergence: { index, claim, reason, detail } };
}

/** Whether a cell's output fingerprint moved across a bind (full compare). */
function fingerprintMoved(a: CellFingerprint, b: CellFingerprint): boolean {
  return a.contentType !== b.contentType ||
    a.hidden !== b.hidden ||
    a.height !== b.height ||
    a.hasContent !== b.hasContent;
}

function fpDetail(a: CellFingerprint, b: CellFingerprint): string {
  return `${a.contentType ?? "∅"}/${a.hasContent}/${a.height ?? "∅"}→` +
    `${b.contentType ?? "∅"}/${b.hasContent}/${b.height ?? "∅"}`;
}
